import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Tab, Tabs } from '@material-ui/core';
import capitalize from '@material-ui/core/utils/capitalize';
import DetailWrapper from '../../../../components/CardRow/DetailWrapper';
import CustomTextField from '../../../../components/CustomizedComponents/CustomTextField';
import DefaultButton from '../../../../components/Button/DefaultButton';
import ButtonWrapper from '../../../../components/Button/ButtonWrapper';
import TabPanel from '../../../../components/TabPanel/TabPanel';
import NoData from '../../../../components/CustomizedComponents/DisplayData/NoData';
import CustomSnackBar from '../../../../components/CustomizedComponents/CustomSnackbar';
import { getMonthName } from '../../../../utils/date';
import { useIntl, FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useGetEndDates, usePostEndDates } from '../../hooks/api/configurationApi';

const handleTabChange = (newValue, fetchedData, setDatesRelatedToYear, setTabValue) => {
  if (fetchedData) {
    setDatesRelatedToYear(fetchedData?.[newValue]);
  }
  setTabValue(newValue);
};

const handleBtnClick = (setEditable, editable, postEndDates, fetchedData, setOpen, idButton) => {
  setEditable(!editable);
  if (idButton !== 'cancel') {
    postEndDates(fetchedData);
    setOpen(true);
  }
};

const ContractEndDate = ({ editable, setEditable }) => {
  const { getEndDates, data: getData, error: getError } = useGetEndDates();
  const { postEndDates, error: postError, loading: postLoading } = usePostEndDates();
  const { handleSubmit, register, errors: inputErrors } = useForm();
  const intl = useIntl();

  const success = intl.formatMessage({ id: 'app.action.saved' });
  const dataMissing = intl.formatMessage({ id: 'app.data.missing' });

  const [fetchedData, setFetchedData] = useState();
  const [tabValue, setTabValue] = useState();
  const [datesRelatedToYear, setDatesRelatedToYear] = useState([]);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(success);
  const [issues, setIssues] = useState(null);

  const handleCustomTextFieldChange = (event, monthItem) => {
    event.stopPropagation();
    const { value } = event.target;

    const monthsByYearIndex = datesRelatedToYear.findIndex((e) => {
      return e.month === monthItem.month;
    });

    setDatesRelatedToYear((oldState) => {
      const newState = [...oldState];
      newState[monthsByYearIndex].date = value;

      return newState;
    });
  };

  useEffect(() => {
    getEndDates();
  }, [getEndDates]);

  useEffect(() => {
    if (getData) {
      const firstKey = Object.keys(getData)[0];
      setFetchedData(getData);
      setTabValue(firstKey);
      setDatesRelatedToYear(getData[firstKey]);
    }
  }, [getData]);

  useEffect(() => {
    if (fetchedData) {
      const currentYearIndex = Object.keys(fetchedData).findIndex((el) => {
        return el === tabValue;
      });
      setFetchedData((oldState) => {
        const newState = { ...oldState };
        Object.values(newState)[currentYearIndex] = datesRelatedToYear;
        return newState;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, datesRelatedToYear]);

  useEffect(() => {
    const currentError = getError || postError;
    if (currentError) {
      setMessage(currentError?.data?.message);
      setIssues(currentError?.data?.issues);
    } else {
      setMessage(success);
      setIssues(null);
    }
  }, [success, getError, postError]);

  return (
    <DetailWrapper>
      {tabValue && (
        <Tabs
          value={tabValue}
          onChange={(event) => handleTabChange(event.target.innerText, fetchedData, setDatesRelatedToYear, setTabValue)}
          variant="scrollable">
          {fetchedData && Object.keys(fetchedData).map((year) => <Tab key={year} label={year} value={year} />)}
        </Tabs>
      )}
      <form onSubmit={handleSubmit(() => handleBtnClick(setEditable, editable, postEndDates, fetchedData, setOpen))}>
        <TabPanel value={tabValue} index={tabValue}>
          {datesRelatedToYear ? (
            datesRelatedToYear.map((monthItem) => (
              <CustomTextField
                key={monthItem.month}
                name={monthItem.month.toString()}
                onChange={(event) => handleCustomTextFieldChange(event, monthItem)}
                label={capitalize(getMonthName(intl, new Date(2000, monthItem.month - 1, 1)))}
                value={monthItem.date}
                variant="outlined"
                type="date"
                disabled={!editable}
                defaultValue={monthItem.date}
                inputRef={register({
                  required: dataMissing,
                })}
                helperText={inputErrors[monthItem.month]?.message}
                error={!!inputErrors[monthItem.month]}
              />
            ))
          ) : (
            <NoData>
              <FormattedMessage id={'app.dataTable.body.noData'} />
            </NoData>
          )}
        </TabPanel>

        <ButtonWrapper>
          <DefaultButton
            id={'cancel'}
            primary={false}
            disabled={!editable}
            title={'app.buttons.cancel'}
            onClick={(event) => handleBtnClick(setEditable, editable, postEndDates, fetchedData, setOpen, event.currentTarget.id)}
          />
          <DefaultButton primary={true} disabled={!editable} type="submit" title={'app.buttons.save'} loaders={[postLoading]} />
        </ButtonWrapper>
      </form>
      {!postLoading && message && <CustomSnackBar status={!postError && !getError ? 'success' : 'error'} message={message} setOpen={setOpen} open={open} />}
      {postError && getError && issues && issues.map((issue) => <CustomSnackBar key={issue} status={'error'} message={issue} setOpen={setOpen} open={open} />)}
    </DetailWrapper>
  );
};

ContractEndDate.propTypes = {
  editable: PropTypes.bool,
  setEditable: PropTypes.func,
};

export default ContractEndDate;
